/**
 * "Анализ речи" card
 *  - Talk ratio (agent / client split bar)
 *  - Interruptions
 *  - Filler words
 *  - Stop words
 *  - Profanity detection — у организации с Э2 встроенных (#2518) только при
 *    результате `agent_profanity` по старому полю, иначе встроенный стоит в
 *    группе «Критерии» (`lib/records/builtin-legacy-rows`)
 */

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '@/lib/utils';
import {
  resolveSpeakerSide,
  type SpeakerRoleMap,
  type SpeakerRoleValue,
} from '@/lib/speaker-roles';
import {
  legacyProfanity,
  speechMetricsVisible,
  type BuiltinPausedByAelo,
} from '@/lib/records/builtin-legacy-rows';
import type { BuiltinResultSources } from '@aica/api-types/schemas';
import {
  AnalysisSection,
  Tag,
  scoreTextColor,
  type XMLAnalysisResult,
} from './analysis-utils';

interface DiarizationSegment {
  speaker: string;
  start: number;
  end: number;
}

interface SpeechSectionProps {
  result: XMLAnalysisResult;
  diarization?: DiarizationSegment[];
  agentName?: string;
  /** `builtinSources` ответа критериев записи; нет — Э2 выключен. */
  builtinSources?: BuiltinResultSources;
  /** `builtinPausedByAelo` ответа критериев записи. */
  builtinPausedByAelo?: BuiltinPausedByAelo;
}

type TalkRole = SpeakerRoleValue;

/**
 * Which side a diarization label belongs to, `unknown` when nothing places it.
 *
 * The ladder itself lives next to `resolveSpeakerRole` in
 * `@/lib/speaker-roles`, shared with `SpeakerTalkRatio`: the two used to hold
 * private copies, and the copies had already diverged on the agent's name —
 * so the two ratio bars on this screen could put one speaker on different
 * sides.
 */
function resolveRole(
  speaker: string,
  roles?: SpeakerRoleMap,
  agentName?: string
): TalkRole {
  return resolveSpeakerSide(speaker, roles, agentName) ?? 'unknown';
}

/**
 * Talk-time split per side.
 *
 * Unresolved time is reported as unresolved rather than folded into the client.
 * The single exception is the two-speaker call where one side is known: with
 * exactly two labels, "not the agent" IS the other side — that is a deduction,
 * not the "assume client" fallback that used to report `Client 100%` for a
 * balanced conversation whenever the labels were raw ASR ids.
 */
function calculateTalkRatio(
  segments: DiarizationSegment[],
  agentName?: string,
  roles?: SpeakerRoleMap
): Record<TalkRole, number> | null {
  if (segments.length === 0) return null;

  const labels = new Set(segments.map((seg) => seg.speaker));
  const resolved = new Map<string, TalkRole>();
  for (const label of labels) {
    resolved.set(label, resolveRole(label, roles, agentName));
  }

  const sides = new Set(resolved.values());
  if (labels.size === 2 && sides.has('unknown') && sides.size === 2) {
    // Two speakers, one side identified — the other one is the counterpart.
    const [knownSide] = [...sides].filter((role) => role !== 'unknown');
    const counterpart: TalkRole = knownSide === 'agent' ? 'client' : 'agent';
    for (const [label, role] of resolved) {
      if (role === 'unknown') resolved.set(label, counterpart);
    }
  }

  const totals = { agent: 0, client: 0, unknown: 0 };
  for (const seg of segments) {
    totals[resolved.get(seg.speaker) ?? 'unknown'] += seg.end - seg.start;
  }

  const total = totals.agent + totals.client + totals.unknown;
  if (total === 0) return null;

  // Largest remainder, so the three shares always add up to exactly 100.
  const exact = {
    agent: (totals.agent / total) * 100,
    client: (totals.client / total) * 100,
    unknown: (totals.unknown / total) * 100,
  };
  const percents = {
    agent: Math.floor(exact.agent),
    client: Math.floor(exact.client),
    unknown: Math.floor(exact.unknown),
  };
  let remainder = 100 - (percents.agent + percents.client + percents.unknown);
  const byRemainder = (['agent', 'client', 'unknown'] as const)
    .slice()
    .sort((a, b) => exact[b] - percents[b] - (exact[a] - percents[a]));
  for (const key of byRemainder) {
    if (remainder <= 0) break;
    percents[key] += 1;
    remainder -= 1;
  }

  return percents;
}

/**
 * What a word-list metric says when it has no words to show.
 *
 * "None" is a claim about the CALL ("we looked, nothing matched"); it may only
 * be made when the metric's own count is zero. A positive count with an empty
 * list is a different statement — the sanitizer (safety-gates.ts) can strip the
 * examples while keeping the count — and printing "None" there would contradict
 * the number standing right next to it. The print report already draws that
 * distinction (print-report/sections/speech.ts), and the PDF must mirror the
 * panel, not out-explain it.
 */
function EmptyWordList({
  count,
  testId,
  t,
}: {
  count: number;
  testId: string;
  t: TFunction;
}) {
  return count === 0 ? (
    <span className="text-success" data-testid={`${testId}-none`}>
      {t('records.analysisPanel.none', 'None')}
    </span>
  ) : (
    <span className="text-fg-subtle" data-testid={`${testId}-no-details`}>
      {t('records.analysisPanel.noDetails', 'no details recorded')}
    </span>
  );
}

export function SpeechSection({
  result,
  diarization,
  agentName,
  builtinSources,
  builtinPausedByAelo,
}: SpeechSectionProps) {
  const { t } = useTranslation('records');

  if (!result.speech) return null;

  const interruptions = result.emotional?.interruptions;
  const fillerWords = result.speech.fillerWords;
  const stopWords = result.speech.stopWords;
  const profanity = legacyProfanity(result, builtinSources, builtinPausedByAelo);

  const talkRatio =
    diarization && diarization.length > 0
      ? calculateTalkRatio(diarization, agentName, result.speakerRoles)
      : null;

  if (!talkRatio && !speechMetricsVisible(result, builtinSources, builtinPausedByAelo)) {
    return null;
  }

  return (
    <AnalysisSection title={t('records.analysisPanel.speechAnalysis', 'Speech Analysis')}>
      <div className="flex flex-col gap-3 text-xs">
        {/* Talk ratio — full-width split bar */}
        {talkRatio && (
          <div>
            <div className="mb-1.5 text-fg-muted">
              {t('records.analysisPanel.talkRatio', 'Talk ratio')}
            </div>
            <div className="flex h-2 overflow-hidden rounded-full bg-bg-subtle">
              <div
                className="h-full bg-primary"
                style={{ width: `${talkRatio.agent}%` }}
              />
              <div
                className="h-full bg-accent"
                style={{ width: `${talkRatio.client}%` }}
              />
              <div
                className="h-full bg-fg-subtle"
                style={{ width: `${talkRatio.unknown}%` }}
              />
            </div>
            {/* The unresolved share is printed only when there is one: on a
                normally attributed call it would be a permanent "0%" claiming
                a doubt that does not exist. */}
            <div className="mt-1.5 flex justify-between gap-2 font-mono text-micro">
              <span className="text-primary">
                {t('records.analysisPanel.agent', 'Agent')} {talkRatio.agent}%
              </span>
              {talkRatio.unknown > 0 && (
                <span className="text-fg-subtle" data-testid="talk-ratio-unknown">
                  {t('records.analysisPanel.talkRatioUnknown', 'Role unknown')}{' '}
                  {talkRatio.unknown}%
                </span>
              )}
              <span className="text-accent">
                {t('records.analysisPanel.client', 'Client')} {talkRatio.client}%
              </span>
            </div>
          </div>
        )}

        {/* Interruptions */}
        {interruptions && (
          <div className="flex items-center gap-2">
            <span className="flex-1 text-fg">
              {t('records.analysisPanel.interruptions', 'Interruptions')}
            </span>
            <span className="font-mono text-micro text-fg-subtle">
              {interruptions.count}{' '}
              {t('records.analysisPanel.interruptionsBreakdown', {
                agent: interruptions.byAgent,
                client: interruptions.byClient,
                defaultValue: '(agent: {{agent}} · client: {{client}})',
              })}
            </span>
            {interruptions.score !== undefined && (
              <span
                className={cn(
                  'w-7 text-right font-mono font-semibold',
                  scoreTextColor(interruptions.score)
                )}
              >
                {interruptions.score}
              </span>
            )}
          </div>
        )}

        {/* Filler words — rendered on the FIELD's presence, same rule as the two
            rows below. This row is the first one a reviewer looks at, and it was
            the one still vanishing on a clean call: an empty card is
            indistinguishable from a detector that never ran (2026-08-15 release
            review). Since #819 `count` is a deterministic tally of the agent's
            hesitation sounds, and `topWords` keeps only the most frequent forms
            without per-word numbers; older analyses carry the model's own figure
            and its chosen examples. Either way a non-zero count with no examples
            must still report the count and the score instead of hiding the
            whole metric. */}
        {fillerWords && (
          <div>
            <div className="flex items-center gap-2">
              <span className="flex-1 text-fg">
                {t('records.analysisPanel.fillerWords', 'Filler words')}{' '}
                <span className="text-fg-subtle">({fillerWords.count})</span>
              </span>
              {fillerWords.topWords.length === 0 && (
                <EmptyWordList
                  count={fillerWords.count}
                  testId="speech-filler-words"
                  t={t}
                />
              )}
              {fillerWords.score !== undefined && (
                <span
                  className={cn(
                    'w-7 text-right font-mono font-semibold',
                    scoreTextColor(fillerWords.score)
                  )}
                >
                  {fillerWords.score}
                </span>
              )}
            </div>
            {fillerWords.topWords.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {fillerWords.topWords.map((w) => (
                  <Tag key={w.word} variant="warning">
                    {w.word}
                    {w.count !== undefined && ` ×${w.count}`}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Agent uncertainty — hedging about the agent's own price/deadline,
            from a list hardwired into the prompt. Named "stop words" in the
            data for historical reasons; the customer's own banned vocabulary
            is the separate block below.

            Rendered on the FIELD's presence, not on a non-empty result: a
            metric that vanishes when it finds nothing is indistinguishable
            from one that never ran, which is exactly how the 2026-08-15
            release review read it ("не увидел этого блока… непонятно,
            отрабатывает или нет"). Same reasoning as profanity's "None". */}
        {stopWords && (
          <div>
            <div className="flex items-center gap-2">
              <span className="flex-1 text-fg">
                {t('records.analysisPanel.agentUncertainty', 'Agent uncertainty')}{' '}
                <span className="text-fg-subtle">({stopWords.count})</span>
              </span>
              {stopWords.topWords.length === 0 && (
                <EmptyWordList count={stopWords.count} testId="speech-stop-words" t={t} />
              )}
              {stopWords.score !== undefined && (
                <span
                  className={cn(
                    'w-7 text-right font-mono font-semibold',
                    scoreTextColor(stopWords.score)
                  )}
                >
                  {stopWords.score}
                </span>
              )}
            </div>
            {stopWords.topWords.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {stopWords.topWords.map((w) => (
                  <Tag key={w.word} variant="danger">
                    {w.word} ×{w.count}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profanity */}
        {profanity && (
          <div>
            <div className="flex items-center gap-2">
              <span className="flex-1 text-fg">
                {t('records.analysisPanel.profanity', 'Profanity')}
              </span>
              {profanity.detected ? (
                <span className="text-danger">
                  {t('records.analysisPanel.detected', 'Detected')}
                </span>
              ) : (
                <span className="text-success">
                  {t('records.analysisPanel.none', 'None')}
                </span>
              )}
            </div>
            {profanity.detected &&
              profanity.instances &&
              profanity.instances.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {profanity.instances.map((inst, idx) => (
                    <Tag key={`${inst.phrase}-${inst.speaker}-${idx}`} variant="danger">
                      {inst.phrase} ×{inst.count}
                      {inst.speaker !== 'unknown' &&
                        ` · ${
                          inst.speaker === 'agent'
                            ? t('records.analysisPanel.speakerAgent', 'agent')
                            : t('records.analysisPanel.speakerClient', 'client')
                        }`}
                    </Tag>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>
    </AnalysisSection>
  );
}
